import React, { useState, useEffect, useRef, useCallback } from 'react';
import config from '../config/config';

const HeroSection = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const containerRef = useRef(null);
    const intervalRef = useRef(null);

    const slides = React.useMemo(() => [
        {
            id: 1,
            title: "企业愿景",
            subtitle: "坚守传承者精神，做亲情的坚定守护者",
            media: config.carouselFirst,
            duration: 15000,
            buttonText: "了解更多",
            buttonLink: "/#about"
        },
        {
            id: 2,
            title: "企业使命",
            subtitle: "坚持科技为爱服务，为每一个家庭打造安全可靠的数字家园",
            media: config.carouselSecond,
            duration: 5000,
            buttonText: "了解族语",
            buttonLink: "/#services"
        },
    ], []);

    const goToSlide = useCallback((index) => {
        setCurrentSlide(index);
    }, []);

    // 离开视口时暂停轮播，减少手机端后台重绘和内存占用
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => setIsVisible(entry.isIntersecting),
            { threshold: 0.1 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!isVisible) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            return;
        }

        const duration = slides[currentSlide].duration || 5000;
        intervalRef.current = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, duration);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [currentSlide, isVisible, slides]);

    const slide = slides[currentSlide];

    return (
        <div ref={containerRef} className="relative h-screen w-full overflow-hidden bg-slate-900">
            <div className="relative h-full w-full">
                {/* 只渲染当前幻灯片，避免手机同时解码多张全屏大图 */}
                <img
                    key={slide.id}
                    src={slide.media}
                    alt={slide.title}
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover"
                />

                <div className="relative z-10 flex h-full flex-col justify-center items-center text-center text-white px-4">
                    <h1 className="text-4xl md:text-6xl font-bold mb-6">
                        {slide.title}
                    </h1>
                    <p className="text-xl md:text-2xl max-w-3xl mb-8">
                        {slide.subtitle}
                    </p>
                    <a
                        href={slide.buttonLink}
                        className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-blue-900 bg-white rounded-2xl shadow-xl hover:bg-blue-50 transition-all duration-300"
                    >
                        {slide.buttonText}
                        <svg className="w-5 h-5 ml-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </a>
                </div>
            </div>

            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex space-x-3">
                {slides.map((_, index) => (
                    <button
                        key={index}
                        className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentSlide ? 'bg-white' : 'bg-white/50'}`}
                        onClick={() => goToSlide(index)}
                        aria-label={`切换到幻灯片 ${index + 1}`}
                    />
                ))}
            </div>
        </div>
    );
};

export default HeroSection;
